import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useRef, useEffect, useState } from 'react';
import Layout from '../components/Layout';
import HeartButton from '../components/HeartButton';
import { getChangemakers, getInterests, getActivities, toggleChangemakerLike } from '../api/api';
import { Users } from 'lucide-react';
import { InterestIcon } from '../components/admin/AdminInterests';
import { useAuth } from '../context/AuthContext';
import { useChangemakerAuth } from '../context/ChangemakerAuthContext';
import { VerifiedBadge, UnverifiedBadge } from '../components/VerifiedBadge';
import { track, setCurrentPage } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const ChangemakersPage = () => {
  const { user, refresh } = useAuth();
  const { cmUser } = useChangemakerAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoLikeDone = useRef(false);

  useEffect(() => { setCurrentPage('organizations'); track('page_view', { page: 'organizations' }); }, []);

  const { data: changemakers = [], isLoading, isError, refetch: refetchChangemakers } = useQuery({
    queryKey: ['changemakers'],
    queryFn: async () => {
      const response = await getChangemakers({});
      return response.data;
    },
    staleTime: 300_000, // 5 min
  });

  const { data: interests = [] } = useQuery({
    queryKey: ['interests'],
    queryFn: async () => {
      const response = await getInterests();
      return response.data;
    },
  });

  useQuery({
    queryKey: ['all-activities'],
    queryFn: async () => {
      const response = await getActivities({});
      return response.data;
    },
  });

  useEffect(() => {
    const autoLikeId = searchParams.get('autoLikeChangemaker');
    if (!autoLikeId || !user || autoLikeDone.current) return;
    autoLikeDone.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('autoLikeChangemaker');
    setSearchParams(next, { replace: true });
    localStorage.removeItem('karmana_pending_like_changemaker');
    const alreadyLiked = (user?.liked_changemakers || []).includes(autoLikeId);
    if (!alreadyLiked) {
      toggleChangemakerLike(autoLikeId).then(() => {
        track('post_signup_action_completed', { page: 'changemakers', changemaker_id: autoLikeId });
        refresh();
      }).catch(() => {});
    }
  }, [user, searchParams]);

  return (
    <Layout>
      <SEOHead
        title="Sustainability Organizations & Changemakers in Bengaluru"
        description="Discover NGOs, changemakers, and organizations driving sustainability and social impact in Bengaluru. Follow the leaders making a difference."
        canonical="https://karmana.in/organizations"
      />
      {/* Header row: heading centered, button on the right */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start gap-4">
            {/* Symmetric spacer so heading stays truly centered */}
            <div className="flex-1 hidden sm:block" />
            <div className="flex-1 text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Discover Changemakers</h1>
              <p className="text-gray-600 text-sm">Find organizations and individuals making a difference</p>
            </div>
            <div className="flex-1 hidden sm:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Changemakers list */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          /* Initial fetch — skeleton grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-3 bg-gray-100 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : isError && changemakers.length === 0 ? (
          /* API failed, no cached data */
          <div className="text-center py-16" data-testid="changemakers-error-state">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium mb-1">Unable to load changemakers</p>
            <p className="text-gray-400 text-sm mb-5">Check your connection and try again</p>
            <button
              onClick={() => refetchChangemakers()}
              className="px-5 py-2 bg-green-600 text-white rounded-full text-sm font-semibold hover:bg-green-700 transition-colors"
              data-testid="changemakers-retry-btn"
            >
              Retry
            </button>
          </div>
        ) : changemakers.length === 0 ? (
          /* API returned zero records */
          <div className="text-center py-16" data-testid="changemakers-empty-state">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No changemakers available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {changemakers.map((changemaker) => (
              <Link
                key={changemaker.id}
                to={`/organizations/${changemaker.slug || changemaker.id}`}
                onClick={() => track('org_card_click', { organisation_id: changemaker.id, organisation_name: changemaker.name, source_page: 'organisations' })}
                className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-4 flex flex-col gap-3"
                data-testid={`changemaker-card-${changemaker.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {changemaker.logo_url ? (
                      <img loading="lazy" src={changemaker.logo_url} alt={changemaker.name} className="w-full h-full object-contain bg-white rounded-xl" />
                    ) : (
                      <Users className="w-6 h-6 text-blue-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug min-w-0">
                      {changemaker.name}
                    </h3>
                    {changemaker.is_claimed
                      ? <VerifiedBadge />
                      : <UnverifiedBadge orgId={changemaker.id} orgName={changemaker.name} />}
                  </div>
                  <HeartButton changemakerId={changemaker.id} trigger="changemaker_card_liked" size={18} className="flex-shrink-0" />
                </div>

                {changemaker.areas_of_interest && changemaker.areas_of_interest.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {changemaker.areas_of_interest.slice(0, 3).map((interestId) => {
                      const interest = interests.find((i) => i.id === interestId);
                      if (!interest) return null;
                      return (
                        <span key={interestId} className="px-2 py-0.5 bg-white text-blue-700 text-[11px] rounded-full flex items-center gap-1 border border-blue-200">
                          <InterestIcon icon={interest.icon} size={11} className="text-blue-600 flex-shrink-0" />
                          <span>{interest.name}</span>
                        </span>
                      );
                    })}
                  </div>
                )}

                <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">
                  {changemaker.short_description}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>

    </Layout>
  );
};

export default ChangemakersPage;
